import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { he, enUS } from 'date-fns/locale';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoon } from '@fortawesome/free-regular-svg-icons';
import { SearchDate } from 'services/flight';
import { addDays, formatPriceCurrency, getDaysDifference } from 'utils/utils';
import { Calendar } from 'react-date-range';
import { useClickAway } from 'hooks/useClickAway';
import useRecaptcha from 'hooks/useRecaptcha';
import { getDatesByCity } from 'services/flight';
import { useSearchParams } from 'react-router-dom';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { dateFormat } from 'utils/shared/utils';
import { getDisabledDates } from 'utils/flight/flight';

interface CalendarAlpProps {
  dates?: SearchDate[];
  searchType: string;
  departureDate: string;
  arrivalDate?: string;
  isLoading?: boolean;
  scrollOnFocusMobile?: boolean;
  onChangeStartDate: (value: Date) => void;
  onChangeEndDate: (value: Date) => void;
  flightSourcesDefault: string;
  arrivalCode: string;
}

const CalendarAlp: FC<CalendarAlpProps> = ({
  dates,
  arrivalDate,
  isLoading,
  scrollOnFocusMobile,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLDivElement | null>(null);
  const initScrollPosition = useRef(0);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [returnDates, setReturnDates] = useState<SearchDate[] | undefined>(undefined);
  const { handleRecapcha, } = useRecaptcha();
  const [urlParams] = useSearchParams();

  const disabledDates = useMemo(() => {
    if (dates && dates.length) {
      return getDisabledDates(dates);
    }

    const allDates: Date[] = [];
    const start = addDays(1);
    const end = addDays(730);
    const current = new Date(start);

    while (current <= end) {
      allDates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return allDates;
  }, [dates]);


  useEffect(() => {
    if (urlParams.get('departureDate')) {
      setStartDate(new Date(urlParams.get('departureDate')!));
    }

    if (urlParams.get('arrivalDate')) {
      setEndDate(new Date(urlParams.get('arrivalDate')!));
    }
  }, [urlParams]);

  const minDate = useMemo(() => {
    if (dates?.length) {
      return dates[0].date;
    }

    return addDays(1);
  }, [dates]);

  const maxDate = useMemo(() => {
    if (dates?.length) {
      return dates[dates.length - 1].date;
    }

    return addDays(730);
  }, [dates]);

  const getLocaleFns = () => {
    const language = localStorage.getItem('language');
    if (language === 'en') {
      return enUS;
    } else {
      return he;
    }
  };

  const nights = useMemo(() => {
    if (!startDate || !endDate) {
      return undefined;
    }

    return getDaysDifference(startDate.toString(), endDate.toString());
  }, [startDate, endDate]);

  const returnDisabledDates = useMemo(() => {
    if (!startDate) {
      const allDates: Date[] = [];
      const current = new Date(minDate);

      while (current <= maxDate) {
        allDates.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }

      return allDates;
    }

    if (returnDates?.length) {
      return getDisabledDates(returnDates);
    }

    const allDates: Date[] = [];
    const current = new Date(minDate);

    while (current <= maxDate) {
      allDates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return allDates;
  }, [startDate, minDate, maxDate, returnDates]);

  const renderPriceDay = (date: Date) => {
    if (!dates?.length) {
      return null;
    }

    const depDate = dates.find((item) => item.date.getTime() === date.getTime());

    if (!depDate) {
      return null;
    }

    return (
      <span className="rdrDayCustom">
        <span className="date">{depDate.date.getDate()}</span>
        <span className="price">{formatPriceCurrency(depDate.minPrice, '$')}</span>
      </span>
    );
  };


  const openDatepicker = () => {
    setOpen(true);
    setTimeout(() => {
      const days = document.querySelectorAll('.rdrWeekDay');

      days.forEach((day) => {
        day.innerHTML = day.innerHTML.replace('יום', '');
      });

      if (window.innerWidth < 768 && scrollOnFocusMobile && inputRef.current) {
        initScrollPosition.current = window.scrollY;
        const offset = 40;
        const elementPosition = inputRef.current.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
      }
    });
  };

  const close = () => {
    setOpen(false);

    if (window.innerWidth < 768 && scrollOnFocusMobile) {
      window.scrollTo({ top: initScrollPosition.current, behavior: 'smooth' });
      initScrollPosition.current = 0;
    }
  };

  const ref = useClickAway(close);

  const onChangeStartDate = async (value: Date) => {
    setStartDate(value);
    setReturnDates(undefined);
    props.onChangeStartDate(value);

    if (props.searchType === '0') {
      return;
    }

    const token = await handleRecapcha();

    if (!token) return;

    const returnDates = await getDatesByCity({
      recaptchaToken: token,
      cityCode: props.arrivalCode,
      isBackDates: true,
      checkIn: dateFormat(value, 'yyyy-MM-dd', 'en'),
      flightSourcesDefault: props.flightSourcesDefault,
    });

    setReturnDates(returnDates);
  };

  const onChangeEndDate = (value: Date) => {
    setEndDate(value);
    props.onChangeEndDate(value);
    close();
  };

  return (
    <div className="position-relative">
      <div
        className={
          'form-control form-group-calendar' +
          (' movable-placeholder-input') +
          (' form-group-calendar-vertical')
        }
        onClick={openDatepicker}
        ref={inputRef}
      >
        {startDate && (
          <>
            <div className="form-group-calendar-vertical-big">
              {dateFormat(startDate, 'dd')}
            </div>
            <div className="form-group-calendar-vertical-dates">
              <div className="text-bold">
                {dateFormat(startDate, 'MMM')
                  .split('')
                  .map((m, i) => (
                    <span key={i}>{m}</span>
                  ))}
              </div>
              <div>
                {dateFormat(startDate, 'eee')
                  .split('')
                  .map((m, i) => (
                    <span key={i}>{m}</span>
                  ))}
              </div>
            </div>
          </>
        )}
        {startDate && endDate && props.searchType === '1' && <div className="form-group-calendar-vertical-div"></div>}
        {endDate && props.searchType === '1' && (
          <>
            <div className="form-group-calendar-vertical-big">
              {dateFormat(endDate, 'dd')
                .split('')
                .map((m, i) => (
                  <span key={i}>{m}</span>
                ))}
            </div>
            <div className="form-group-calendar-vertical-dates">
              <div className="text-bold">
                {dateFormat(endDate, 'MMM')
                  .split('')
                  .map((m, i) => (
                    <span key={i}>{m}</span>
                  ))}
              </div>
              <div>
                {dateFormat(endDate, 'eee')
                  .split('')
                  .map((m, i) => (
                    <span key={i}>{m}</span>
                  ))}
              </div>
            </div>
          </>
        )}
        {nights !== undefined && props.searchType === '1' && (
          <span className="ph-10">
            <span className="text-bold">{nights} </span>
            <FontAwesomeIcon icon={faMoon} />
          </span>
        )}
      </div>

      {open && (
        <div ref={ref} className="daterangepicker-wr daterangepicker-wr-right">
          {isLoading && <div className="alp-calendar-loading" />}
          {!isLoading && (
            <div className='flex-row' style={{ flexWrap: 'unset' }}>
              <Calendar
                minDate={minDate}
                maxDate={maxDate}
                disabledDates={disabledDates}
                dayContentRenderer={renderPriceDay}
                date={startDate}
                months={1}
                locale={getLocaleFns()}
                color={getComputedStyle(document.body).getPropertyValue('--color-contrast')}
                onChange={onChangeStartDate}
              />

              {props.searchType === '1' && (
                <Calendar
                  minDate={minDate}
                  maxDate={maxDate}
                  disabledDates={returnDisabledDates}
                  dayContentRenderer={renderPriceDay}
                  date={endDate}
                  months={1}
                  locale={getLocaleFns()}
                  color={getComputedStyle(document.body).getPropertyValue('--color-contrast')}
                  onChange={onChangeEndDate}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default withErrorBoundary(CalendarAlp, 'CalendarAlp');
